/* A ZIP file, with no dependency and no compression.

   A corner screen exports two files that only make sense as a pair — put one on
   the wrong panel and the illusion is inside out — so they leave as one
   download rather than two prompts a browser is free to reorder or block.

   STORE (method 0), deliberately. PNG and H.264 are already entropy-coded, so
   DEFLATE over them buys around nothing for a real cost per byte; the whole
   point of the container here is to keep the pair together, not to make it
   smaller. `CompressionStream('deflate-raw')` would give method 8 for free in
   Chromium if that ever changed.

   Written out by hand because the alternative is a dependency for ~70 lines of
   well-specified structure, and this file has no reason ever to change: the
   format is thirty years old. */

/* CRC-32, the ordinary reflected table. Built once on first use rather than at
   module load, since a project that never exports a corner never needs it. */
let TABLE = null;
function crcTable(){
  if(TABLE) return TABLE;
  TABLE = new Int32Array(256);
  for(let n = 0; n < 256; n++){
    let c = n;
    for(let k = 0; k < 8; k++) c = (c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1);
    TABLE[n] = c;
  }
  return TABLE;
}
function crc32(bytes){
  const t = crcTable();
  let c = ~0;
  for(let i = 0; i < bytes.length; i++) c = t[(c ^ bytes[i]) & 0xFF] ^ (c >>> 8);
  return (~c) >>> 0;
}

/* DOS time, which is what the format stores: two seconds of resolution, and an
   epoch of 1980. Anything earlier than that cannot be represented; clamped
   rather than allowed to wrap into a nonsense date. */
function dosStamp(d){
  const year = Math.max(1980, d.getFullYear());
  return {
    time: (d.getHours() << 11) | (d.getMinutes() << 5) | (d.getSeconds() >> 1),
    date: ((year - 1980) << 9) | ((d.getMonth() + 1) << 5) | d.getDate()
  };
}

const MAX = 0xFFFFFFFF;   // no ZIP64 here — see the guard in zipStore

/**
 * @param {{name:string, blob:Blob}[]} entries
 * @returns {Promise<Blob>} a .zip holding them uncompressed
 */
export async function zipStore(entries){
  const enc = new TextEncoder();
  const files = [];
  for(const e of entries){
    const bytes = new Uint8Array(await e.blob.arrayBuffer());
    files.push({ name: enc.encode(e.name), bytes, crc: crc32(bytes) });
  }

  /* Refused rather than truncated. Without ZIP64 the offsets and sizes are 32
     bits, and silently wrapping them produces an archive that looks fine and
     unpacks to garbage — much worse than not writing one. Nothing this app
     exports comes close, so this is a guard, not a limitation. */
  const total = files.reduce((n, f) => n + f.bytes.length, 0);
  if(total > MAX || files.some(f => f.bytes.length > MAX))
    throw new Error('That export is too large to package — save the panels one at a time.');

  const { time, date } = dosStamp(new Date());
  const parts = [];
  let offset = 0;
  const central = [];

  for(const f of files){
    const head = new DataView(new ArrayBuffer(30));
    head.setUint32(0, 0x04034b50, true);   // local file header
    head.setUint16(4, 20, true);           // version needed
    head.setUint16(6, 0x0800, true);       // UTF-8 names
    head.setUint16(8, 0, true);            // method: store
    head.setUint16(10, time, true);
    head.setUint16(12, date, true);
    head.setUint32(14, f.crc, true);
    head.setUint32(18, f.bytes.length, true);   // compressed == uncompressed
    head.setUint32(22, f.bytes.length, true);
    head.setUint16(26, f.name.length, true);
    head.setUint16(28, 0, true);           // no extra field
    parts.push(new Uint8Array(head.buffer), f.name, f.bytes);

    const dir = new DataView(new ArrayBuffer(46));
    dir.setUint32(0, 0x02014b50, true);    // central directory entry
    dir.setUint16(4, 20, true);            // version made by
    dir.setUint16(6, 20, true);
    dir.setUint16(8, 0x0800, true);
    dir.setUint16(10, 0, true);
    dir.setUint16(12, time, true);
    dir.setUint16(14, date, true);
    dir.setUint32(16, f.crc, true);
    dir.setUint32(20, f.bytes.length, true);
    dir.setUint32(24, f.bytes.length, true);
    dir.setUint16(28, f.name.length, true);
    dir.setUint32(42, offset, true);       // where this file's local header is
    central.push(new Uint8Array(dir.buffer), f.name);

    offset += 30 + f.name.length + f.bytes.length;
  }

  const dirSize = central.reduce((n, p) => n + p.length, 0);
  const end = new DataView(new ArrayBuffer(22));
  end.setUint32(0, 0x06054b50, true);      // end of central directory
  end.setUint16(8, files.length, true);
  end.setUint16(10, files.length, true);
  end.setUint32(12, dirSize, true);
  end.setUint32(16, offset, true);

  return new Blob([...parts, ...central, new Uint8Array(end.buffer)],
                  { type: 'application/zip' });
}
